import moment from 'moment';

export type Filter<T = any> = {
  name: string;
  value: T;
  label: string;
  isAddedByDefault?: boolean;
  isOptionHidden?: boolean;
};

type DefaultFilters = Record<string, Filter[]>;

export const DASHBOARD_FILTER_NAMES = {
  OPEN: 'open',
  WAITER: 'waiter',
  INTERNAL: 'internal',
  PARTS_OUT_OF_STOCK: 'partsOutOfStock',
  NEEDS_VIDEO: 'needsVideo',
  NEEDS_PARTS_ORDERED: 'needsPartsOrdered',
  DATE_RANGE: 'dateRange',
  DATE_CLOSED: 'dateClosed',
  PRIMARY_ADVISOR_ID: 'primaryAdvisorId',
  PRIMARY_TECHNICIAN_ID: 'primaryTechnicianId',
  VIN: 'vin',
  TEAM: 'team',
} as const;

/**
 * ISA-2402 story 3.1: the one reserved value meaning "repair orders with no Team", carried
 * INSIDE the `team` array rather than as a separate query parameter (AD-8).
 *
 * Its server counterpart is `TeamFilterSelection::UNASSIGNED_SENTINEL`
 * (`iservice-api/src/DataModel/RepairOrder/TeamFilterSelection.php`), which maps it to the
 * `team IS NULL OR team = ''` branch (Clarification C-1). The two must stay byte-identical:
 * changing this value changes a wire contract on four Surfaces at once (Advisor, Technician and
 * Parts Dashboards, and the Digital Route Sheet), plus Parts Express and the native list.
 *
 * It is double-underscored because `'unassigned'` and `'Unassigned'` are both plausible
 * dealer-authored Team names, and a collision would make one real Team unfilterable.
 */
export const TEAM_UNASSIGNED_VALUE = '__unassigned__';

/**
 * ISA-2402 story 4.2 review fix (MEDIUM) — Team copy that renders on more than one Surface,
 * single-sourced so `TeamMultiSelect` (the filter interaction, extracted by story 4.2) and
 * `TeamAssignmentControl` (the read/write assignment control on `TeamCard`, story 2.5) cannot
 * drift apart on wording. `NO_TEAM_LABEL` is AD-12's "No Team" — never the bare word "Unassigned",
 * which already means "no Service Advisor / Technician / Parts Agent assigned" elsewhere in the
 * product. ISA-4788 removed the Team-options helper notes, including the failed-fetch copy.
 */
export const TEAM_OPTION_COPY = {
  NO_TEAM_LABEL: 'No Team',
} as const;

export const REPORTING_FILTER_NAMES = {
  DATE_CREATED: 'createdDateRange',
  DATE_CLOSED: 'closedDateRange',
  STATUS: 'status',
  MAKE: 'make',
} as const;

export const STATUS_OPTION_LABELS = {
  OPEN: 'Open',
  CLOSED: 'Closed',
} as const;

export const STATUS_OPTION_VALUES = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
} as const;

export type StatusOptionValue = (typeof STATUS_OPTION_VALUES)[keyof typeof STATUS_OPTION_VALUES];

export const RO_TYPE_OPTION_LABELS = {
  ALL: 'All',
  INTERNAL: 'Internal',
  CUSTOMER: 'Customer',
} as const;

// Values match the existing `internal` query param: omitted (All), true (Internal), false (Customer).
export const RO_TYPE_OPTION_VALUES = {
  ALL: undefined,
  INTERNAL: true,
  CUSTOMER: false,
} as const;

export type RoTypeOptionValue = (typeof RO_TYPE_OPTION_VALUES)[keyof typeof RO_TYPE_OPTION_VALUES];

export const defaultFilters: DefaultFilters = {
  advisorReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  advisorUsageReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  checkinReporting: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  closedNoVideosReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  customerEngagementReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  dashboard: [
    { name: DASHBOARD_FILTER_NAMES.OPEN, value: true, label: 'Open', isAddedByDefault: true },
    { name: DASHBOARD_FILTER_NAMES.WAITER, value: true, label: 'Waiter' },
    {
      name: DASHBOARD_FILTER_NAMES.INTERNAL,
      value: false,
      label: 'RO Type',
    },
    {
      name: DASHBOARD_FILTER_NAMES.PARTS_OUT_OF_STOCK,
      value: false,
      label: 'Parts Out Of Stock',
    },
    { name: DASHBOARD_FILTER_NAMES.NEEDS_VIDEO, value: true, label: 'Needs Video' },
    {
      name: DASHBOARD_FILTER_NAMES.NEEDS_PARTS_ORDERED,
      value: false,
      label: 'Needs Parts Ordered',
    },
    { name: DASHBOARD_FILTER_NAMES.DATE_RANGE, value: [], label: 'Date Created' },
    { name: DASHBOARD_FILTER_NAMES.DATE_CLOSED, value: [], label: 'Date Closed' },
    {
      name: DASHBOARD_FILTER_NAMES.PRIMARY_ADVISOR_ID,
      value: '',
      label: 'By Advisor',
    },
    {
      name: DASHBOARD_FILTER_NAMES.PRIMARY_TECHNICIAN_ID,
      value: '',
      label: 'By Technician',
    },
    { name: DASHBOARD_FILTER_NAMES.VIN, value: '', label: 'By VIN', isOptionHidden: true },
    // ISA-2402 story 3.1: reaches the Advisor, Technician and Parts Dashboards, which all render
    // `Dashboard.tsx` against this list. The Digital Route Sheet does NOT -- it mounts
    // `DashboardRepairOrderFilter` directly with `serviceExpress` below, so that array carries
    // its own copy of this entry and the two must be kept in step.
    { name: DASHBOARD_FILTER_NAMES.TEAM, value: [], label: 'Team', isAddedByDefault: true },
  ],
  demo: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
    {
      name: 'assignedUserId',
      value: '',
      label: 'By Assigned User',
    },
  ],

  serviceExpress: [
    {
      name: 'primaryAdvisorId',
      value: '',
      label: 'Primary Advisor',
      isAddedByDefault: true,
    },
    {
      name: 'primaryTechnicianId',
      value: '',
      label: 'Technician',
    },
    {
      name: 'primaryPartAdvisorId',
      value: '',
      label: 'Parts Advisor',
    },
    {
      name: 'waiter',
      value: true,
      label: 'Waiter',
    },
    {
      name: 'internal',
      value: false,
      label: 'RO Type',
      isAddedByDefault: true,
    },
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: false,
    },
    // ISA-2402 story 3.1: the Digital Route Sheet's own filter list -- deliberately a second
    // entry, not a duplicate by accident. It does not inherit `dashboard` above.
    {
      name: DASHBOARD_FILTER_NAMES.TEAM,
      value: [],
      label: 'Team',
      isAddedByDefault: true,
    },
  ],
  serviceExpressCycleTime: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  reconditioningExpressCycleTime: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  reconditioning: [
    {
      name: 'primaryAdvisorId',
      value: '',
      label: 'Primary Advisor',
      isAddedByDefault: true,
    },
    {
      name: 'primaryTechnicianId',
      value: '',
      label: 'Technician',
    },
    {
      name: 'primaryPartAdvisorId',
      value: '',
      label: 'Parts Advisor',
    },
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: false,
    },
  ],
  declinedServiceReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  financeReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'Transaction Date',
      isAddedByDefault: true,
    },
  ],
  followUpReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  iPayReport: [
    {
      name: 'transactionDate',
      value: [moment().startOf('day').toDate(), moment().endOf('day').toDate()],
      label: 'Transaction Date',
      isAddedByDefault: true,
    },
    {
      name: 'dateSent',
      value: [],
      label: 'Date Requested',
    },
  ],
  partsReporting: [
    {
      name: 'dateRange',
      value: [],
      label: 'Transaction Date',
      isAddedByDefault: true,
    },
  ],
  mpiReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
    {
      name: 'primaryAdvisorId',
      value: '',
      label: 'By Advisor',
    },
    {
      name: 'primaryTechnicianId',
      value: '',
      label: 'By Technician',
    },
    {
      name: 'mpiTemplateId',
      value: '',
      label: 'By MPI Template',
    },
  ],
  mobileCheckinReport: [
    {
      name: REPORTING_FILTER_NAMES.DATE_CREATED,
      value: [],
      label: 'RO Creation Date',
      isAddedByDefault: false,
    },
    {
      name: REPORTING_FILTER_NAMES.DATE_CLOSED,
      value: [],
      label: 'RO Closed Date',
      isAddedByDefault: true,
    },
    {
      name: REPORTING_FILTER_NAMES.STATUS,
      value: STATUS_OPTION_VALUES.CLOSED,
      label: 'Status',
      isAddedByDefault: false,
    },
    {
      name: REPORTING_FILTER_NAMES.MAKE,
      value: [],
      label: 'Make',
    },
  ],
  recommendationReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
    {
      name: 'primaryAdvisorId',
      value: '',
      label: 'By Advisor',
    },
    {
      name: 'primaryTechnicianId',
      value: '',
      label: 'By Technician',
    },
  ],
  servicePhaseTrendReporting: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
    {
      name: 'interval',
      value: 'day',
      label: 'By Interval',
      isAddedByDefault: true,
    },
  ],
  vehicleReconditioningTrendReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
    {
      name: 'interval',
      value: 'day',
      label: 'By Interval',
      isAddedByDefault: true,
    },
  ],
  vehicleReconditioningUserTrendReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
    {
      name: 'interval',
      value: 'day',
      label: 'By Interval',
      isAddedByDefault: true,
    },
    {
      name: 'reconditioningPhaseId',
      value: '',
      label: 'By Reconditioning Phase',
    },
    {
      name: 'role',
      value: '',
      label: 'By User Role',
    },
    {
      name: 'userIds',
      value: '',
      label: 'By User',
    },
  ],
  technicianReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  technicianUsageReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
    {
      name: REPORTING_FILTER_NAMES.MAKE,
      value: [],
      label: 'Make',
    },
  ],
  upgradeReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  advisorRecommendationReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  technicianRecommendationReport: [
    {
      name: 'dateRange',
      value: [],
      label: 'By Date',
      isAddedByDefault: true,
    },
  ],
  management: [
    { name: 'open', value: true, label: 'Open', isAddedByDefault: true },
    { name: 'waiter', value: true, label: 'Waiter' },
    {
      name: 'internal',
      value: false,
      label: 'RO Type',
      isAddedByDefault: true,
    },
    { name: 'needsVideo', value: true, label: 'Needs Video' },
    { name: 'dateRange', value: [], label: 'By Date' },
    {
      name: 'primaryAdvisorId',
      value: '',
      label: 'By Advisor',
    },
    {
      name: 'primaryTechnicianId',
      value: '',
      label: 'By Technician',
    },
  ],
};
